import { useEffect, useMemo } from 'react';
import {
  FormControl, FormHelperText, InputLabel, MenuItem, Select,
} from '@material-ui/core';
import { useSelector } from 'react-redux';
import { Store, useAppDispatch } from 'src/redux/Store';
import { ErrorBag } from 'src/models/ErrorModel';
import { FORM_ID } from 'src/models/FormModel';
import { getEventTicketsAction } from 'src/redux/actions/ticketActions';

interface RowIdentifier {
  name: string;
  start: number;
  end: number;
}

interface Props {
  identifiersString: string;
  eventId?: number;
  productId?: number;
  value?: string;
  onChange: (value: string) => void;
  form: FORM_ID;
  name: string;
  disabled?: boolean;
}

const parseIdentifiers = (identifiersString: string): RowIdentifier[] => {
  if (!identifiersString) return [];
  return identifiersString.split(',').reduce((result: RowIdentifier[], row: string) => {
    const [name, start, end] = row.split(':');
    if (name && start && end) {
      result.push({
        name,
        start: parseInt(start, 10),
        end: parseInt(end, 10),
      });
    }
    return result;
  }, []);
};

const parseValue = (value?: string): { row: string; seat: string } => {
  if (!value) return { row: '', seat: '' };
  const lastDash = value.lastIndexOf('-');
  if (lastDash === -1) return { row: '', seat: '' };
  return {
    row: value.substring(0, lastDash),
    seat: value.substring(lastDash + 1),
  };
};

const IdentifierSelector = (props: Props) => {
  const {
    identifiersString,
    eventId,
    productId,
    value,
    onChange,
    form,
    name,
    disabled,
  } = props;

  const dispatch = useAppDispatch();
  const takenIdentifiers = useSelector(
    (state: Store) => state.tickets?.takenIdentifiers || [] as string[],
  );

  const errors: ErrorBag =
    useSelector((state: Store) => state.errors[form] || {} as ErrorBag);
  const errorText = errors[name] ? errors[name].join(', ') : undefined;

  useEffect(() => {
    if (eventId && productId) {
      dispatch(getEventTicketsAction(eventId, productId));
    }
  }, [eventId, productId, dispatch]);

  const rows = useMemo(() => parseIdentifiers(identifiersString), [identifiersString]);

  // Rows that have at least one available seat
  const availableRows = useMemo(() => rows.filter((row) => {
    for (let i = row.start; i <= row.end; i += 1) {
      if (!takenIdentifiers.includes(`${row.name}-${i}`)) {
        return true;
      }
    }
    return false;
  }), [rows, takenIdentifiers]);

  const { row: selectedRow, seat: selectedSeat } = parseValue(value);

  const availableSeats = useMemo(() => {
    if (!selectedRow) return [];
    const row = availableRows.find((r) => r.name === selectedRow);
    if (!row) return [];
    const seats: string[] = [];
    for (let i = row.start; i <= row.end; i += 1) {
      const identifier = `${row.name}-${i}`;
      if (!takenIdentifiers.includes(identifier)) {
        seats.push(String(i));
      }
    }
    return seats;
  }, [selectedRow, availableRows, takenIdentifiers]);

  const handleRowChange = (rowName: string) => {
    const row = availableRows.find((r) => r.name === rowName);
    if (!row) return;
    // Find first available seat in this row
    for (let i = row.start; i <= row.end; i += 1) {
      const identifier = `${rowName}-${i}`;
      if (!takenIdentifiers.includes(identifier)) {
        onChange(identifier);
        return;
      }
    }
    onChange(`${rowName}-`);
  };

  const handleSeatChange = (seat: string) => {
    onChange(`${selectedRow}-${seat}`);
  };

  if (rows.length === 0) {
    return null;
  }

  if (availableRows.length === 0) {
    return <span style={{ color: 'red', fontWeight: 'bold' }}>Sold Out</span>;
  }

  if (availableRows.length === 1) {
    // Single row — just show seat selector
    const onlyRow = availableRows[0];
    const seats: string[] = [];
    for (let i = onlyRow.start; i <= onlyRow.end; i += 1) {
      const identifier = `${onlyRow.name}-${i}`;
      if (!takenIdentifiers.includes(identifier)) {
        seats.push(String(i));
      }
    }
    return (
      <FormControl style={{ width: '100%', minWidth: 120 }} disabled={disabled} error={!!errorText}>
        <InputLabel id={`${onlyRow.name}-label`}>{onlyRow.name}</InputLabel>
        <Select
          labelId={`${onlyRow.name}-label`}
          value={selectedSeat}
          onChange={(e) => handleSeatChange(e.target.value as string)}
          disabled={disabled}>
          {seats.map((seat) => (
            <MenuItem value={seat} key={`${onlyRow.name}-${seat}`}>{seat}</MenuItem>
          ))}
        </Select>
        {errorText && <FormHelperText>{errorText}</FormHelperText>}
      </FormControl>
    );
  }

  // Multiple rows — show row selector + seat selector
  return (
    <div style={{ display: 'flex', gap: '8px' }}>
      <FormControl style={{ width: '50%', minWidth: 120 }} disabled={disabled} error={!!errorText}>
        <InputLabel id="row-selector-label">Row</InputLabel>
        <Select
          labelId="row-selector-label"
          value={selectedRow}
          onChange={(e) => handleRowChange(e.target.value as string)}
          disabled={disabled}>
          {availableRows.map((row) => (
            <MenuItem value={row.name} key={row.name}>{row.name}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl style={{ width: '50%', minWidth: 120 }} disabled={disabled || !selectedRow} error={!!errorText}>
        <InputLabel id="seat-selector-label">Seat</InputLabel>
        <Select
          labelId="seat-selector-label"
          value={selectedSeat}
          onChange={(e) => handleSeatChange(e.target.value as string)}
          disabled={disabled || !selectedRow}>
          {availableSeats.map((seat) => (
            <MenuItem value={seat} key={`${selectedRow}-${seat}`}>{seat}</MenuItem>
          ))}
        </Select>
        {errorText && <FormHelperText>{errorText}</FormHelperText>}
      </FormControl>
    </div>
  );
};

export default IdentifierSelector;
